import { useEffect } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import { useI18n } from '@tribeplatform/react-components/i18n'
import { CreatePageButton } from '@tribeplatform/react-components/Templates'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { alert } from '@tribeplatform/react-ui-kit/Dialog'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { useCustomizer, useCustomizerNavigation } from '../hooks/index.js'
import { useCustomizerPreview } from '../hooks/use-customizer-preview.hook.js'
import { CustomizerActionType } from '../types.js'
import { PreviewIFrame } from './PreviewIFrame.js'
import { PreviewRedoUndo } from './PreviewRedoUndo.js'
import { PreviewSaveButton } from './PreviewSaveButton.js'
import { PreviewScreenSizePicker } from './PreviewScreenSizePicker.js'

export const Preview = () => {
  const { $t } = useI18n()
  const location = useLocation()
  const { dispatch } = useCustomizer()

  const {
    editNavigation,
    previewMode,
    navigationSlates: { headerDisabled, sidebar1Disabled },
    navigations,
  } = useCustomizerNavigation()
  const {
    previewSize,
    previewDarkMode,
    setPreviewDarkMode,
    hasDarkMode,
    previewDarkModeDisabled,
  } = useCustomizerPreview()
  const navigate = useNavigate()

  const finalPreviewSize = editNavigation ? 'lg' : previewSize

  useEffect(() => {
    if (window?.innerWidth < 1024) {
      alert({
        title: $t({
          defaultMessage: 'Not available on small screens',
          id: 'Admin.Editor.Preview.SmallScreenAlert',
        }),
        description: $t(
          {
            defaultMessage:
              'Customizer is not available on smaller screens. To access, please view it on screens above {min_width}px width.',
            id: 'Admin.Editor.Preview.SmallScreenAlert.Description',
          },
          {
            min_width: 1024,
          },
        ),
      }).then(() => {
        if (navigate.length > 2) {
          navigate(-1)
        } else {
          navigate('/manage/people/members')
        }
      })
    }
  }, [$t, navigate])

  useEffect(() => {
    dispatch({
      type: CustomizerActionType.RESET_UPDATES,
    })
  }, [dispatch, location])

  return (
    <div className="bg-background-backdrop h-full sm:px-5 py-5">
      <div className="space-y-4">
        <div className="flex space-between items-center justify-between">
          <div className="flex items-center space-s-3">
            <PreviewScreenSizePicker />
            {hasDarkMode && (
              <IconButton
                size="md"
                variant="secondaryNeutral"
                onClick={() => setPreviewDarkMode(!previewDarkMode)}
                icon={
                  previewDarkMode ? (
                    <SvgIcon name="sun" />
                  ) : (
                    <SvgIcon name="moon" />
                  )
                }
                accessibilityLabel={
                  previewDarkMode
                    ? $t({
                        defaultMessage: 'Light mode',
                        id: 'Generics.LightMode',
                      })
                    : $t({
                        defaultMessage: 'Dark mode',
                        id: 'Generics.DarkMode',
                      })
                }
                tooltip
                disabled={previewDarkModeDisabled}
              />
            )}
            {previewMode !== 'template' && <PreviewRedoUndo />}
          </div>
          <div className="flex justify-end">
            {previewMode === 'template' ? (
              <CreatePageButton />
            ) : (
              <PreviewSaveButton />
            )}
          </div>
        </div>
        <PreviewIFrame
          previewSize={finalPreviewSize}
          darkMode={previewDarkMode}
          previewMode={previewMode}
          headerDisabled={headerDisabled}
          sidebar1Disabled={sidebar1Disabled}
          navigations={navigations}
        />
      </div>
    </div>
  )
}
